import type { FeatureFlag } from '@risksmart-app/modules/src/index';

/**
 * Per-entity Smart Copy descriptor for Custom Data Sources — the single source
 * of truth for the register's gate + tooltip wording. Mirrors
 * `controlTestSmartCopy.ts` / `indicatorSmartCopy.ts`.
 *
 * - `flagKey` — per-org feature flag read via `useIsFeatureFlagEnabled`,
 *   resolved from OpenFeature (flagd locally/ephemeral, Datadog on
 *   dev-cloud/prod) or the seeded Meta.features override for e2e test orgs;
 *   registered in `packages/feature-flags/src/flags.ts`.
 * - `eventLabel` — prefixes the Amplitude event names (capitalised); consumed
 *   when analytics land in a follow-up.
 * - `entityNoun` — interpolates into the disabled "copy" tooltip (lowercase).
 */
export const CUSTOM_DATASOURCE_SMART_COPY = {
  flagKey: 'smart_copy_custom_datasource' satisfies FeatureFlag,
  eventLabel: 'Custom Data Source',
  entityNoun: 'custom data source',
} as const;

/**
 * Router location-state contract for opening the create page pre-populated from
 * a selected source: the register's "Copy" action passes the source id, and the
 * create page reads it, refetches the source and builds the copied defaults. A
 * refresh drops the state, falling back to a blank create form.
 */
export type CopyCustomDatasourceNavState = {
  copyFromCustomDatasourceId?: string;
};

/**
 * Extracts the copy-from source id from untyped router location state, returning
 * undefined for a normal (blank) create navigation.
 */
export const readCopyFromCustomDatasourceId = (
  state: unknown
): string | undefined =>
  state !== null &&
  typeof state === 'object' &&
  'copyFromCustomDatasourceId' in state &&
  typeof state.copyFromCustomDatasourceId === 'string'
    ? state.copyFromCustomDatasourceId
    : undefined;
